// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { NotificationTemplate } from './useNotificationTemplates';
import { toast } from 'sonner';

// Event-related categories and trigger events
const EVENT_CATEGORIES = ['registration', 'events', 'reminders', 'payments'];
const EVENT_TRIGGER_EVENTS = [
  'registration_confirmed',
  'event_reminder',
  'event_cancelled', 
  'check_in_instructions',
  'payment_confirmed',
  'event_updated',
  'venue_changed'
];

export const useEventNotificationTemplates = () => {
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchEventTemplates = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('notification_templates')
        .select('*')
        .or(`category.in.(${EVENT_CATEGORIES.join(',')}),trigger_event.in.(${EVENT_TRIGGER_EVENTS.join(',')})`)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching event templates:', error);
        toast.error('Failed to load event templates');
        return;
      }

      setTemplates(data || []);
    } catch (error) {
      console.error('Error fetching event templates:', error);
      toast.error('Failed to load event templates');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEventTemplates();
  }, []);

  const createTemplate = async (templateData: Omit<NotificationTemplate, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      // Ensure the template is marked as event-related
      const eventTemplateData = {
        ...templateData,
        category: templateData.category || 'events',
        type: templateData.type || 'email'
      };

      const { data, error } = await supabase
        .from('notification_templates')
        .insert([eventTemplateData])
        .select()
        .single();

      if (error) {
        console.error('Error creating template:', error);
        toast.error('Failed to create template');
        return null;
      }

      await fetchEventTemplates();
      toast.success('Template created successfully');
      return data;
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Failed to create template');
      return null;
    }
  };

  const updateTemplate = async (id: string, updates: Partial<NotificationTemplate>) => {
    try {
      const { error } = await supabase
        .from('notification_templates')
        .update(updates)
        .eq('id', id);

      if (error) {
        console.error('Error updating template:', error);
        toast.error('Failed to update template');
        return false;
      }

      await fetchEventTemplates();
      toast.success('Template updated successfully');
      return true;
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error('Failed to update template');
      return false;
    }
  };

  const deleteTemplate = async (id: string) => {
    try {
      const { error } = await supabase
        .from('notification_templates')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting template:', error);
        toast.error('Failed to delete template');
        return false;
      }

      await fetchEventTemplates();
      toast.success('Template deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
      return false;
    }
  };

  const duplicateTemplate = async (template: NotificationTemplate) => {
    const newTemplate = {
      ...template,
      title: `${template.title} (Copy)`,
      is_active: true // Copies should be active by default
    };
    
    // Remove id and timestamps for the new template
    const { id, created_at, updated_at, ...templateToDuplicate } = newTemplate;
    
    return await createTemplate(templateToDuplicate);
  };

  return {
    templates,
    isLoading,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    duplicateTemplate,
    refetch: fetchEventTemplates
  };
};